/*
 * Blog post TOC — progressive enhancement of the `nav.blog-toc` markup that
 * the blog-migration importer prepends to article bodies (contract:
 * docs/blog-migration-block-contract.md).
 *
 * Two behaviours, both optional (anchors keep working without JS):
 *   - Scroll spy: marks the chapter currently in view with
 *     `aria-current="true"` on its TOC link (styled blue + 4px bar).
 *   - Bottom sheet (<1280px, matching the CSS threshold): injects a floating
 *     blue "Jump to section" button (design-system .cta--button) + a light
 *     backdrop; opening reveals the section list as a sheet above the button
 *     (Jan review, task 36320). The chrome is inert on desktop, where the
 *     CSS renders the sticky left rail instead.
 */

function init() {
  const content = document.querySelector('.blog-post-content');
  const toc = content?.querySelector('nav.blog-toc');
  if (!toc || toc.dataset.enhanced) return;

  const links = [...toc.querySelectorAll('a[href^="#"]')];
  if (!links.length) return;
  toc.dataset.enhanced = 'true';

  const tocLabel = content.dataset.tocLabel || '';

  // --- Drawer chrome -------------------------------------------------
  toc.classList.add('blog-toc--enhanced');
  if (tocLabel) toc.setAttribute('aria-label', tocLabel);

  const backdrop = document.createElement('div');
  backdrop.className = 'blog-toc__backdrop';

  const toggle = document.createElement('button');
  toggle.type = 'button';
  // Design-system primary pill (blue/white) — the float positioning is the
  // block's own CSS, everything visual comes from .cta--button.
  toggle.className = 'blog-toc__toggle cta cta--button';
  toggle.setAttribute('aria-expanded', 'false');
  const label = document.createElement('span');
  label.textContent = tocLabel;
  toggle.append(label);

  toc.prepend(toggle);
  toc.prepend(backdrop);

  const setOpen = (open) => {
    toc.classList.toggle('blog-toc--open', open);
    toggle.setAttribute('aria-expanded', String(open));
  };

  toggle.addEventListener('click', () => setOpen(!toc.classList.contains('blog-toc--open')));
  backdrop.addEventListener('click', () => setOpen(false));
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') setOpen(false);
  });

  // --- Scroll spy -----------------------------------------------------
  const linkByTarget = new Map();
  for (const link of links) {
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = document.getElementById(id);
    if (target) linkByTarget.set(target, link);
  }

  const setCurrent = (link) => {
    for (const other of links) other.removeAttribute('aria-current');
    link?.setAttribute('aria-current', 'true');
  };

  for (const link of links) {
    link.addEventListener('click', () => {
      setCurrent(link);
      setOpen(false);
    });
  }

  if (linkByTarget.size === 0) return;

  // "Current" = the last chapter whose top has crossed the upper third of
  // the viewport. Position-based (not IntersectionObserver) so short final
  // chapters near the page bottom still win once scrolled past.
  const update = () => {
    const line = window.innerHeight * 0.35;
    let current = links[0];
    for (const [target, link] of linkByTarget) {
      if (target.getBoundingClientRect().top <= line) current = link;
    }
    setCurrent(current);
  };

  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    },
    { passive: true }
  );
  update();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
